import { Component, input, computed } from '@angular/core';
import { NgStyle } from '@angular/common';
import { QRCodeComponent } from 'angularx-qrcode';
import { LayerRenderData, LayerType } from '../../models/layer-render-data';
import { LayoutStyleType } from '../../models/layout-style-type';

/**
 * LiveData keys a QR widget can encode. Mirrors `ExitLiveDataKeys` on the backend — the values
 * arrive in `componentData` because `DisplayRenderService.MergeComponentData` merges the whole
 * LiveData dictionary into every ComponentWidget layer.
 */
const QR_LIVE_DATA_KEY = {
    /** Pre-payment: the settlement page the parker pays on (`{paymentApp}/pay/{parkingProcessId}`). */
    PaymentQrUrl: 'exit.payment_qr_url',
    /** Post-payment: the fiscal receipt URL. */
    ReceiptUrl: 'exit.receipt_url',
} as const;

/**
 * `componentType` values this component knows how to draw.
 *
 * The operator authors `componentType` as free text on the layer, so the widget name is the whole
 * contract — there is no per-layer JSON editor to carry a source key. Each name therefore binds to
 * exactly one LiveData key: an unknown name, or a known name whose key is absent from this render,
 * draws nothing rather than guessing which URL was meant.
 */
const QR_WIDGETS: Record<string, string> = {
    paymentqrwidget: QR_LIVE_DATA_KEY.PaymentQrUrl,
    receiptqrwidget: QR_LIVE_DATA_KEY.ReceiptUrl,
};

@Component({
    selector: 'app-layer',
    standalone: true,
    imports: [NgStyle, QRCodeComponent],
    templateUrl: './layer.component.html',
    styleUrls: ['./layer.component.scss'],
})
export class LayerComponent {
    readonly layer = input.required<LayerRenderData>();
    readonly layoutStyle = input<LayoutStyleType>(LayoutStyleType.Stack);

    readonly LayerType = LayerType;

    /**
     * Text is authored in single-line inputs, so operators type the escape "\n"
     * (and legacy templates carry <br>). Convert both to real newline characters;
     * the `.layer--text` rule sets `white-space: pre-wrap`, which renders them as
     * line breaks. Interpolation stays safe (no innerHTML).
     */
    readonly displayText = computed(() =>
        (this.layer().textContent ?? '')
            .replace(/\\n/g, '\n')
            .replace(/<br\s*\/?>/gi, '\n')
    );

    readonly isAnchored = computed(() => {
        if (this.layoutStyle() === LayoutStyleType.Relative) return true;
        const pos = this.layer().position;
        return Boolean(pos?.top || pos?.left || pos?.right || pos?.bottom);
    });

    readonly positionStyles = computed(() => {
        const pos = this.layer().position;
        return {
            zIndex: this.layer().zIndex,
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            right: pos?.right ?? 0,
            bottom: pos?.bottom ?? 0,
            width: pos?.width ?? undefined,
            height: pos?.height ?? undefined,
        };
    });

    /**
     * URL this widget should encode as a QR, or null when it is not a QR widget or the backend sent
     * no URL for it this render.
     *
     * The QR is generated here on the display, from a URL the backend built. The alternative — the
     * backend rendering a PNG and the display fetching it — puts an extra network round-trip between
     * "parker is standing at the exit" and "there is something to scan", on the hop most likely to be
     * down at a free-flow site.
     */
    readonly qrData = computed<string | null>(() => {
        const l = this.layer();
        if (l.layerType !== LayerType.ComponentWidget) return null;

        const key = QR_WIDGETS[(l.componentType ?? '').trim().toLowerCase()];
        if (!key) return null;

        const value = l.componentData?.[key];
        return typeof value === 'string' && value.length > 0 ? value : null;
    });

    /**
     * QR render size in pixels, taken from the layer's own box so an operator sizes the code the same
     * way they size every other layer.
     *
     * Only an explicit `px` value is used. A percentage is a real and likely value here — new layers
     * are created at `100%` — and parsing it as a number would silently render a 100px QR inside a
     * full-width band. Since the modules are drawn into a canvas that CSS then fits to the layer
     * (`max-width: 100%`), an unresolvable size costs resolution, not layout: fall back to a
     * generous default and let the box do the sizing.
     */
    readonly qrWidth = computed(() => {
        const pos = this.layer().position;
        const px = (raw: string | null | undefined): number | null => {
            const match = /^\s*(\d+(?:\.\d+)?)px\s*$/i.exec(raw ?? '');
            const value = match ? Number.parseFloat(match[1]) : NaN;
            return Number.isFinite(value) && value > 0 ? Math.round(value) : null;
        };
        return px(pos?.width) ?? px(pos?.height) ?? 400;
    });
}
